/**
 * The typefaces an admin can choose, and how to load one.
 *
 * ── WHY THE LIST LIVES IN CODE AND THE CHOICE LIVES IN SETTINGS ─────────────
 *
 * A typeface is the loudest single thing about how a product feels, and picking
 * one should not need a developer, a build and a deploy. So the CHOICE is an
 * admin setting (Brand → Headings / Body typeface).
 *
 * The LIST is here, closed, because a free-text font name is a font that
 * silently fails to load — and the person who typed it never finds out, since
 * the real one is installed on their own machine while every buyer sees the
 * fallback. Each entry below is served by Google Fonts and carries the weights
 * this design actually uses.
 *
 * ── AND WHY NUMBERS ARE NOT IN IT ───────────────────────────────────────────
 *
 * NAFDAC registration numbers, batch codes, seal numbers and order references
 * stay monospace whatever is chosen here (§11). Those are the strings a buyer
 * compares character by character against a physical pack, and a face where 1,
 * l and I converge turns a counterfeit check into a coin toss.
 */

export interface FontChoice {
  /** The `family` as Google Fonts spells it. Empty for the system stack. */
  family: string;
  /** What goes in `font-family`, fallbacks included. */
  stack: string;
  /** Weights to request. Kept tight: every weight is bytes on a 3G connection. */
  weights: string;
}

/** The system stack — no request, and already on the device. */
const SYSTEM =
  "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const of = (family: string, weights = '400;500;600;700;800'): FontChoice => ({
  family,
  stack: `'${family}', ${SYSTEM}`,
  weights,
});

export const FONTS: Record<string, FontChoice> = {
  'plus-jakarta': of('Plus Jakarta Sans'),
  outfit: of('Outfit'),
  nunito: of('Nunito'),
  manrope: of('Manrope'),
  'dm-sans': of('DM Sans'),
  figtree: of('Figtree'),
  poppins: of('Poppins'),
  sora: of('Sora'),
  'space-grotesk': of('Space Grotesk', '400;500;700'),
  system: { family: '', stack: SYSTEM, weights: '' },
};

/** Unknown keys fall back rather than throw: a setting is data, not a promise. */
export function fontFor(key: string | undefined, fallback: string): FontChoice {
  return FONTS[key ?? ''] ?? FONTS[fallback] ?? FONTS.system!;
}

/**
 * One stylesheet URL for however many families are actually needed.
 *
 * Deduplicated, because headings and body are usually set to the same family
 * and asking for it twice is a second round trip for bytes already in flight.
 * Returns null when nothing needs fetching — the system stack is free.
 */
export function googleFontsHref(choices: FontChoice[]): string | null {
  const families = [
    ...new Map(
      choices
        .filter((choice) => choice.family !== '')
        .map((choice) => [choice.family, choice]),
    ).values(),
  ];

  if (families.length === 0) return null;

  const params = families
    .map((f) => `family=${f.family.replace(/ /g, '+')}:wght@${f.weights}`)
    .join('&');

  // `display=swap`: show the fallback immediately and swap when the real face
  // lands. The alternative is invisible text for as long as the network takes,
  // and on the connections this app is built for that is seconds of blank page.
  return `https://fonts.googleapis.com/css2?${params}&display=swap`;
}
